import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import {
  catchError,
  combineLatest,
  filter,
  map,
  of,
  startWith,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  TIME_RANGES,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  ConfigProperties,
  IdoProperties,
  LocationProfile,
} from 'src/app/core/models/admin';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  ADDRESS_FORM_LABEL,
  BREAK_HOUR,
  CONFIGURATION_LABEL,
  DISTANCE_UNIT_LIST,
  ERROR_MESSAGES,
  FOOTER_LABEL,
  IDO_TYPES,
  IdoSpecificProperties,
  LEAVE_BLDG_OPTIONS,
  LOCATION_START,
  NonIdoConfigProperties,
  OPTIMIZE,
  VEHICLE_LIST,
  WAIT_TIME,
} from '../location-profile.constants';
import { ConfigPropertiesTypes } from './configuration.types';

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss'],
  standalone: false,
})
export class ConfigurationComponent implements OnInit {
  @Input() locationItem!: LocationProfile;
  @Input() edit!: boolean;
  @Output() changeTab = new EventEmitter();
  @Output() formStatus = new EventEmitter();

  configurationForm!: FormGroup;
  copyOfInitialState: Record<string, unknown> = {};

  readonly CONSTANTS = {
    ...CONFIGURATION_LABEL,
    ...VALIDATION_MESSAGE,
    ...FOOTER_LABEL,
    ...ADDRESS_FORM_LABEL,
    ...ERROR_MESSAGES,
  };

  readonly SpecialCharacters = SpecialCharacters;
  readonly REGEX = SpecialCharacters.ALPHABET_NUMBER;
  timeRangeOptions = TIME_RANGES;
  idoTypeOptions = IDO_TYPES;
  locationStartOptions = LOCATION_START;
  breakHourOptions = BREAK_HOUR;
  waitTimeOptions = WAIT_TIME;
  optimizeOptions = OPTIMIZE;
  locationEndOptions = LOCATION_START;
  breakHourEndOptions = BREAK_HOUR;
  puWaitTimeOptions = WAIT_TIME;
  vehicleOptions = VEHICLE_LIST.slice().sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  leaveBldgOptions = LEAVE_BLDG_OPTIONS;
  distanceUnitOptions = DISTANCE_UNIT_LIST;
  idoProps = IdoSpecificProperties;
  nonIdoProp = NonIdoConfigProperties;
  configPropState!: ConfigProperties;
  idoPropState!: IdoProperties;

  formDirty = false;
  isIDO = false;
  idoComplete = false;

  private readonly idoRestoreFields: string[] = [
    'latitude',
    'longitude',
    'idoType',
    'optimizationEnabled',
    'vehicleType',
    'startEndLocation',
    'locationEnd',
    'leaveBuildingTime',
    'breakStart',
    'breakEnd',
    'breakDuration',
    'waitTimeDelivery',
    'waitTimePickup',
    'distanceUnits',
    'edwEnabled',
    'followMyDeliveryEnabled',
    'itineraryReordering',
    'forecastSmartScanEnabled',
    'trimbleDispatchPlanning',
  ];

  private readonly gtsRestoreFields: string[] = [
    'gtsDriverSelected',
    'supGroupDriver',
    'sendDataDriverTime',
    'contractorId',
    'contractorName',
    'supGroupLocation',
    'sendDataLocationTime',
    'additionalTimeCardFeed',
    'additionalContractorId',
    'additionalContractorName',
    'additionalSupGroup',
    'additionalSendDataTime',
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    public readonly commonUtils: CommonUtils,
    private readonly locationService: LocationService,
    private readonly errorHandler: ErrorhandleService,
    private readonly matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.createReturnState();
    this.subscribes();
    if (this.edit) {
      this.isIDO = !!this.locationItem?.routeDeliveryOrder;
    }
  }

  onIdoChange(checked: boolean): void {
    this.isIDO = checked;
    this.configurationForm.get('ido')?.setValue(checked, { emitEvent: false });

    this.setIdoFieldsRequired();

    if (!checked) {
      this.clearIdoFields();
    }
  }

  onGtsTimeCardChange(enabled: boolean): void {
    if (enabled) {
      setTimeout(() => {
        this.configurationForm.get('gtsDriverSelected')?.setValue(false);
      });
    }
  }

  setIdoFieldsRequired(): void {
    if (!this.isIDO) {
      this.configurationForm.get('latitude')?.clearValidators();
      this.configurationForm.get('longitude')?.clearValidators();
      this.configurationForm.get('idoType')?.clearValidators();
      this.configurationForm.get('optimizationEnabled')?.clearValidators();
      this.configurationForm.get('vehicleType')?.clearValidators();
      this.configurationForm.get('startEndLocation')?.clearValidators();
      this.configurationForm.get('locationEnd')?.clearValidators();
      this.configurationForm.get('leaveBuildingTime')?.clearValidators();
      this.configurationForm.get('breakStart')?.clearValidators();
      this.configurationForm.get('breakEnd')?.clearValidators();
      this.configurationForm.get('breakDuration')?.clearValidators();
      this.configurationForm.get('waitTimeDelivery')?.clearValidators();
      this.configurationForm.get('waitTimePickup')?.clearValidators();
      this.configurationForm.get('distanceUnits')?.clearValidators();
      this.configurationForm.get('edwEnabled')?.clearValidators();
      this.configurationForm.get('followMyDeliveryEnabled')?.clearValidators();
      this.configurationForm.get('itineraryReordering')?.clearValidators();
      this.configurationForm.get('forecastSmartScanEnabled')?.clearValidators();
      this.configurationForm.get('trimbleDispatchPlanning')?.clearValidators();
    } else {
      this.configurationForm
        .get('latitude')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('longitude')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('idoType')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('optimizationEnabled')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('vehicleType')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('startEndLocation')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('locationEnd')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('leaveBuildingTime')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('breakStart')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('breakEnd')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('breakDuration')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('waitTimeDelivery')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('waitTimePickup')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('distanceUnits')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('edwEnabled')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('followMyDeliveryEnabled')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('itineraryReordering')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('forecastSmartScanEnabled')
        ?.addValidators([Validators.required]);
      this.configurationForm
        .get('trimbleDispatchPlanning')
        ?.addValidators([Validators.required]);
    }
  }

  checkLatLongValue(value: string): boolean {
    if (value !== undefined && value !== null && value.length > 0) {
      return true;
    }

    return false;
  }

  createReturnState() {
    this.copyOfInitialState = {
      ...this.configurationForm.getRawValue(),
    };
  }

  resetConfigForm(): void {
    // Restore original form values
    this.configurationForm.reset(this.copyOfInitialState);
    this.configurationForm.markAsPristine();
    this.configurationForm.markAsUntouched();

    // Restore IDO from original snapshot
    this.isIDO = !!(
      this.copyOfInitialState.routeDeliveryOrder ??
      this.copyOfInitialState.optimizedDeliveryOrder
    );

    this.idoComplete = this.isIDO ? this.checkIdoProperties() : true;
    this.formDirty = false;
    this.formStatus.emit(false);
  }

  initForm() {
    this.locationItem.idoType = this.checkIdoType(
      this.locationItem?.idoType ?? '',
    );

    const configProperties = this.setConfigProperties(
      this.locationItem.idoType,
    );

    this.setFormValues(configProperties);
  }

  clearIdoFields(): void {
    const clearFields = {
      latitude: '',
      longitude: '',
      idoType: '',
      optimizationEnabled: '',
      vehicleType: '',
      startEndLocation: 'Facility',
      locationEnd: '',
      leaveBuildingTime: '',
      breakStart: '',
      breakEnd: '',
      breakDuration: '',
      waitTimeDelivery: '',
      waitTimePickup: '',
      distanceUnits: 'Kilometers',
      edwEnabled: false,
      followMyDeliveryEnabled: false,
      itineraryReordering: false,
      forecastSmartScanEnabled: false,
      trimbleDispatchPlanning: false,
    };
    this.configurationForm.patchValue(clearFields);
  }

  private nullifyIdoFields(target: LocationProfile): void {
    target.routeDeliveryOrder = false;
    target.optimizedDeliveryOrder = false;
    target.optimizationEnabled = false;
    target.idoType = '';
  }

  checkIdoType(idoType: string): string {
    if (idoType === 'S2C' || idoType === 'S2CO') {
      return 'S2CO';
    } else if (idoType === 'Forecast/ S2C' || idoType === 'FS2C') {
      return 'FS2C';
    } else {
      return '';
    }
  }

  checkIdoProperties(): boolean {
    //fix this in the future
    const test: boolean[] = [];
    // check ido specific properties, all are required
    this.idoProps.forEach((prop) => {
      const temp = this.configurationForm.value[prop];
      if (prop === 'optimizationEnabled') {
        test.push(temp === true || temp === false);
      } else {
        test.push(temp.length > 0);
      }
    });

    return !test.includes(false);
  }

  checkIdoPropsOnAdd(): boolean {
    return this.isIDO ? this.idoComplete : true;
  }

  checkIdoPropsOnEdit(): boolean {
    return this.isIDO ? this.idoComplete && this.formDirty : this.formDirty;
  }

  subscribes(): void {
    this.configurationForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formDirty = this.configurationForm.dirty;
        this.idoComplete = this.isIDO ? this.checkIdoProperties() : true;

        this.formStatus.emit(this.formDirty);
      });
  }

  getConfigData(): LocationProfile {
    const payload: LocationProfile = {
      ...this.locationItem,
      ...this.configurationForm.getRawValue(),
      address: { ...this.locationItem.address },
      locationGroups: this.locationItem.locationGroups,
    };

    payload.gmtTimeZone = this.normalizeGmtTz(payload.gmtTimeZone);

    if (this.isIDO) {
      payload.routeDeliveryOrder = true;
      payload.optimizedDeliveryOrder =
        this.configurationForm.get('optimizationEnabled')?.value ?? false;

      payload.optimizationEnabled =
        this.configurationForm.get('optimizationEnabled')?.value ?? false;

      payload.idoType = this.checkIdoType(payload.idoType);
    } else {
      // payload cleanup
      this.nullifyIdoFields(payload);

      // UI + form cleanup
      this.isIDO = false;
      this.configurationForm.get('ido')?.setValue(false, { emitEvent: false });

      this.clearIdoFields();
      this.setIdoFieldsRequired();
    }

    return payload;
  }

  resetFormState(profile: LocationProfile): void {
    profile.gmtTimeZone = this.normalizeGmtTz(profile.gmtTimeZone);

    this.locationItem = profile;
    this.locationService.setProfileState$(this.locationItem);

    this.initForm();

    // Explicit reset semantics
    this.configurationForm.reset(this.configurationForm.getRawValue());
    this.configurationForm.markAsPristine();
    this.configurationForm.markAsUntouched();

    // Re-evaluate IDO
    this.isIDO = !!(
      this.locationItem?.routeDeliveryOrder ??
      this.locationItem?.optimizedDeliveryOrder
    );
    this.idoComplete = this.isIDO ? this.checkIdoProperties() : true;

    this.formDirty = false;
    this.formStatus.emit(false);
  }

  submitLocationProfile(): void {
    this.locationItem = this.getConfigData();
    if (!this.edit) {
      this.locationService
        .addLocation(this.locationItem, this.locationItem?.locationCode)
        .pipe(
          take(1),
          map((res) => {
            this.matDialog.closeAll();
            if (res?.code === 200 && res?.response) {
              this.resetFormState(res?.response);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
          }),
          catchError((res) => {
            this.errorHandler.handleError<ConfigurationComponent>(
              res,
              res.code,
              'Location Profile: Failed to add profile in configuration component.',
            );
            return of(false);
          }),
          filter((val) => !!val),
        )
        .subscribe();
    } else {
      this.locationService
        .updateLocation(this.locationItem, this.locationItem?.locationCode)
        .pipe(
          take(1),
          map((res) => {
            this.matDialog.closeAll();
            if (res?.code === 200 && res?.response) {
              this.resetFormState(res?.response);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
          }),
          catchError((res) => {
            this.errorHandler.handleError<ConfigurationComponent>(
              res,
              res.code,
              'Location Profile: Failed to update profile in configuration component.',
            );
            return of(false);
          }),
          filter((val) => !!val),
        )
        .subscribe();
    }
  }

  private setFormValues(configProperties: ConfigPropertiesTypes) {
    this.configurationForm = this.fb.group({
      dataRetentionDays: [
        configProperties.dataRetentionDays,
        Validators.compose([Validators.maxLength(2)]),
      ],
      locationType: [
        configProperties.locationType,
        Validators.compose([Validators.maxLength(2)]),
      ],
      messagingEnabled: [configProperties.messagingEnabled],
      gioEnabled: [configProperties.gioEnabled],
      interceptsActive: [configProperties.interceptsActive],
      byodEnabled: [configProperties.byodEnabled],
      driverReleaseEnabled: [configProperties.driverReleaseEnabled],
      safePlacePhotoEnabled: [configProperties.safePlacePhotoEnabled],
      adlActive: [configProperties.adlActive],
      hazmatEnabled: [configProperties.hazmatEnabled],
      onRoadPickupEnabled: [configProperties.onRoadPickupEnabled],
      onCallPickupProvideReceipt: [configProperties.onCallPickupProvideReceipt],
      fileUploadEnabled: [configProperties.fileUploadEnabled],
      centerSlicEnabled: [configProperties.centerSlicEnabled],
      driverLoadVisibility: [configProperties.driverLoadVisibility],
      handoverVisibility: [configProperties.handoverVisibility],
      edwEtaApiOnlyVisibility: [configProperties.edwEtaApiOnlyVisibility],
      gtsTimeCardEnabled: [configProperties.gtsTimeCardEnabled],
      gtsDriverSelected: [configProperties.gtsDriverSelected],
      supGroupDriver: [configProperties.supGroupDriver],
      sendDataDriverTime: [configProperties.sendDataDriverTime],
      supGroupLocation: [configProperties.supGroupLocation],
      sendDataLocationTime: [configProperties.sendDataLocationTime],
      contractorId: [configProperties.contractorId],
      contractorName: [configProperties.contractorName],
      additionalTimeCardFeed: [configProperties.additionalTimeCardFeed],
      additionalContractorId: [configProperties.additionalContractorId],
      additionalContractorName: [configProperties.additionalContractorName],
      additionalSupGroup: [configProperties.additionalSupGroup],
      additionalSendDataTime: [configProperties.additionalSendDataTime],
      ido: [
        !!(
          this.locationItem.routeDeliveryOrder ??
          this.locationItem.optimizedDeliveryOrder
        ),
      ],
      latitude: [
        this.locationItem.latitude,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^-?\d{2,4}(\.\d{2,8})?$/),
        ]),
      ],
      longitude: [
        this.locationItem.longitude,
        Validators.compose([
          Validators.required,
          Validators.pattern(/^-?\d{2,4}(\.\d{2,8})?$/),
        ]),
      ],
      idoType: [configProperties.idoType],
      startEndLocation: [configProperties.startEndLocation],
      breakStart: [configProperties.breakStart],
      waitTimeDelivery: [configProperties.waitTimeDelivery],
      optimizationEnabled: [configProperties.optimizationEnabled],
      locationEnd: [configProperties.startEndLocation],
      breakEnd: [configProperties.breakEnd],
      waitTimePickup: [configProperties.waitTimePickup],
      vehicleType: [configProperties.vehicleType],
      leaveBuildingTime: [configProperties.leaveBuildingTime],
      breakDuration: [
        configProperties.breakDuration,
        Validators.compose([
          Validators.required,
          Validators.min(1),
          Validators.max(119),
        ]),
      ],
      distanceUnits: [configProperties.distanceUnits],

      edwEnabled: [configProperties.edwEnabled],
      followMyDeliveryEnabled: [configProperties.followMyDeliveryEnabled],
      itineraryReordering: [configProperties.itineraryReordering],
      forecastSmartScanEnabled: [configProperties.forecastSmartScanEnabled],
      trimbleDispatchPlanning: [configProperties.trimbleDispatchPlanning],
    });

    this.setupSupGroupDriverConditionalValidation();
    this.setupLocationConditionalValidation();
    this.setIdoFieldsRequired();
  }

  private setupSupGroupDriverConditionalValidation(): void {
    const gtsEnabledCtrl = this.configurationForm.get('gtsTimeCardEnabled')!;
    const gtsDriverSelectedCtrl =
      this.configurationForm.get('gtsDriverSelected')!;
    const supGroupCtrl = this.configurationForm.get('supGroupDriver');

    combineLatest([
      gtsEnabledCtrl.valueChanges.pipe(startWith(gtsEnabledCtrl.value)),
      gtsDriverSelectedCtrl.valueChanges.pipe(
        startWith(gtsDriverSelectedCtrl.value),
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([enabled, driverSelected]) => {
        const required = enabled === true && driverSelected === true;
        this.toggleRequired(supGroupCtrl, required);
        this.configurationForm.updateValueAndValidity({ emitEvent: false });
      });
  }

  private setupLocationConditionalValidation(): void {
    const gtsEnabledCtrl = this.configurationForm.get('gtsTimeCardEnabled')!;
    const gtsDriverSelectedCtrl =
      this.configurationForm.get('gtsDriverSelected')!;
    const additionalFeedCtrl = this.configurationForm.get(
      'additionalTimeCardFeed',
    );

    const enabled$ = gtsEnabledCtrl.valueChanges.pipe(
      startWith(gtsEnabledCtrl.value),
    );
    const driverSelected$ = gtsDriverSelectedCtrl.valueChanges.pipe(
      startWith(gtsDriverSelectedCtrl.value),
    );
    const additionalFeed$ = additionalFeedCtrl
      ? additionalFeedCtrl.valueChanges.pipe(
          startWith(additionalFeedCtrl.value),
        )
      : of(false);

    // Base Location fields required state
    combineLatest([enabled$, driverSelected$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([enabled, driverSelected]) => {
        const isLocation = enabled === true && driverSelected === false;

        if (enabled === false) {
          this.restoreFields(this.gtsRestoreFields);
        }

        this.toggleRequired(
          this.configurationForm.get('contractorId'),
          isLocation,
        );
        this.toggleRequired(
          this.configurationForm.get('contractorName'),
          isLocation,
        );
        this.toggleRequired(
          this.configurationForm.get('supGroupLocation'),
          isLocation,
        );

        // Let the form re-evaluate validity for the Save button
        this.configurationForm.updateValueAndValidity({ emitEvent: false });
      });

    // Additional feed required state (only when Location path + checkbox ON)
    combineLatest([enabled$, driverSelected$, additionalFeed$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([enabled, driverSelected, additional]) => {
        const requireAdditional =
          enabled === true && driverSelected === false && additional === true;

        this.toggleRequired(
          this.configurationForm.get('additionalContractorId'),
          requireAdditional,
        );
        this.toggleRequired(
          this.configurationForm.get('additionalContractorName'),
          requireAdditional,
        );
        this.toggleRequired(
          this.configurationForm.get('additionalSupGroup'),
          requireAdditional,
        );

        this.configurationForm.updateValueAndValidity({ emitEvent: false });
      });
  }

  private restoreFields(fields: string[]): void {
    const patchValues: Record<string, unknown> = {};
    fields.forEach((field) => {
      patchValues[field] = this.copyOfInitialState[field];
    });

    this.configurationForm.patchValue(patchValues, { emitEvent: false });
  }

  /** Toggle only 'required' without touching other validators */
  private toggleRequired(ctrl: AbstractControl | null, required: boolean) {
    if (!ctrl) return;

    if (required) {
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.removeValidators(Validators.required);
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private setConfigProperties(idoType: string): ConfigPropertiesTypes {
    return {
      dataRetentionDays: this.locationItem?.dataRetentionDays ?? '7',
      locationType: this.locationItem?.locationType ?? '00',
      interceptsActive: this.locationItem?.interceptsActive ?? false,
      adlActive: this.locationItem?.adlActive ?? false,
      onCallPickupProvideReceipt:
        this.locationItem?.onCallPickupProvideReceipt ?? false,
      routeDeliveryOrder: this.locationItem?.routeDeliveryOrder ?? false,
      idoType: idoType,
      startEndLocation: this.locationItem?.startEndLocation ?? 'Facility',
      leaveBuildingTime: this.locationItem?.leaveBuildingTime ?? '',
      breakStart: this.locationItem?.breakStart ?? '',
      breakEnd: this.locationItem?.breakEnd ?? '',
      breakDuration: this.locationItem?.breakDuration ?? '',
      waitTimeDelivery: this.locationItem?.waitTimeDelivery ?? '',
      waitTimePickup: this.locationItem?.waitTimePickup ?? '',
      distanceUnits: this.locationItem?.distanceUnits ?? 'Kilometers',
      itineraryReordering: this.locationItem?.itineraryReordering ?? false,
      vehicleType: this.locationItem?.vehicleType ?? '',
      contractorId: this.locationItem?.contractorId ?? '',
      contractorName: this.locationItem?.contractorName ?? '',
      supGroupDriver: this.locationItem?.supGroupDriver ?? '',
      supGroupLocation: this.locationItem?.supGroupLocation ?? '',
      sendDataDriverTime: this.locationItem?.sendDataDriverTime ?? '',
      sendDataLocationTime: this.locationItem?.sendDataLocationTime ?? '',
      sendDataTime: this.locationItem?.sendDataTime ?? '',
      optimizedDeliveryOrder:
        this.locationItem?.optimizedDeliveryOrder ?? false,
      trimbleDispatchPlanning:
        this.locationItem?.trimbleDispatchPlanning ?? false,
      ...this.getAdditionalFields(),
      ...this.getEnabledFields(),
      ...this.getVisibilityFields(),
    };
  }

  private getAdditionalFields() {
    return {
      additionalTimeCardFeed:
        this.locationItem?.additionalTimeCardFeed ?? false,
      additionalContractorId: this.locationItem?.additionalContractorId ?? '',
      additionalContractorName:
        this.locationItem?.additionalContractorName ?? '',
      additionalSendDataTime: this.locationItem?.additionalSendDataTime ?? '',
      additionalSupGroup: this.locationItem?.additionalSupGroup ?? '',
    };
  }

  private getEnabledFields() {
    return {
      messagingEnabled: this.locationItem?.messagingEnabled ?? false,
      gioEnabled: this.locationItem?.gioEnabled ?? false,
      byodEnabled: this.locationItem?.byodEnabled ?? false,
      driverReleaseEnabled: this.locationItem?.driverReleaseEnabled ?? false,
      safePlacePhotoEnabled: this.locationItem?.safePlacePhotoEnabled ?? false,
      hazmatEnabled: this.locationItem?.hazmatEnabled ?? false,
      onRoadPickupEnabled: this.locationItem?.onRoadPickUpEnabled ?? false,
      fileUploadEnabled: this.locationItem?.fileUploadEnabled ?? false,
      centerSlicEnabled: this.locationItem?.centerSlicEnabled ?? true,
      gtsTimeCardEnabled: this.locationItem?.gtsTimeCardEnabled ?? false,
      gtsDriverSelected: this.locationItem?.gtsDriverSelected ?? false,
      edwEnabled: this.locationItem?.edwEnabled ?? false,
      followMyDeliveryEnabled:
        this.locationItem?.followMyDeliveryEnabled ?? false,
      forecastSmartScanEnabled:
        this.locationItem?.forecastSmartScanEnabled ?? false,
      optimizationEnabled: this.locationItem?.optimizationEnabled ?? false,
    };
  }

  private getVisibilityFields() {
    return {
      driverLoadVisibility: this.locationItem?.driverLoadVisibility ?? false,
      handoverVisibility: this.locationItem?.handoverVisibility ?? false,
      edwEtaApiOnlyVisibility:
        this.locationItem?.edwEtaApiOnlyVisibility ?? false,
    };
  }

  private normalizeGmtTz(input?: string): string | undefined {
    if (!input) return input;
    return input.split(/\s|\(/)[0]; // splits by space or '(' and takes the first token
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
